import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { offersAPI, marginAPI, signingRequestsAPI, getErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import IncognitoToggleButton from "../components/IncognitoToggleButton";
import { Separator } from "../components/ui/separator";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Check,
  X,
  Pen,
  Download,
  Sun,
  Battery,
  Zap,
  MapPin,
  Eraser,
  Eye,
  EyeOff,
  FileCheck,
  Loader2,
  Edit,
  ChevronLeft,
  ChevronRight,
  Archive,
  ArchiveRestore,
  Mail,
  Send,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  ExternalLink,
  RefreshCw
} from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Use CDN for PDF.js worker to avoid loading issues in production
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function OfferDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [sellerSignatureDialogOpen, setSellerSignatureDialogOpen] = useState(false);
  const [sendingContract, setSendingContract] = useState(false);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfLoadError, setPdfLoadError] = useState(false);  // Fallback to iframe on error
  const [sendingEmail, setSendingEmail] = useState(false);  // For sending offer via email
  const [marginData, setMarginData] = useState(null);  // Margin calculation for admins
  const [showMarginAdmin, setShowMarginAdmin] = useState(false);  // Admin-Modus: show margin info (default: hidden)
  
  // Remote Signing State
  const [signingRequest, setSigningRequest] = useState(null);
  const [sendForSignatureDialogOpen, setSendForSignatureDialogOpen] = useState(false);
  const [sendingForSignature, setSendingForSignature] = useState(false);
  
  // Admin role check
  const isAdmin = user?.role === "superadmin" || user?.role === "admin";
  
  // Signature Canvas (for customer)
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Seller Signature Canvas
  const sellerCanvasRef = useRef(null);
  const [isSellerDrawing, setIsSellerDrawing] = useState(false);
  const [hasSellerSignature, setHasSellerSignature] = useState(false);

  useEffect(() => {
    loadOffer();
  }, [id]);

  useEffect(() => {
    if (signatureDialogOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [signatureDialogOpen]);

  // Initialize seller signature canvas
  useEffect(() => {
    if (sellerSignatureDialogOpen && sellerCanvasRef.current) {
      const canvas = sellerCanvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [sellerSignatureDialogOpen]);

  const loadOffer = async () => {
    try {
      const response = await offersAPI.get(id);
      setOffer(response.data);
      
      // Load signing request status for this offer
      try {
        const signingResponse = await signingRequestsAPI.list({ offer_id: id });
        const requests = signingResponse.data?.signing_requests || signingResponse.data || [];
        // Find active signing request (not completed, not expired, not revoked)
        const activeRequest = requests.find(r => 
          !['completed_digital', 'completed_manual', 'expired', 'revoked'].includes(r.status)
        );
        // Or find the most recent completed one
        const completedRequest = requests.find(r => 
          ['completed_digital', 'completed_manual'].includes(r.status)
        );
        setSigningRequest(activeRequest || completedRequest || null);
      } catch (signingError) {
        console.log("Signing request not found for offer:", signingError);
      }
      
      // Load margin data for admins
      if (isAdmin) {
        try {
          const marginResponse = await marginAPI.getOfferMargin(id);
          setMarginData(marginResponse.data);
        } catch (marginError) {
          console.log("Margin data not available:", marginError);
          // Silently fail - margin calculation might not be configured
        }
      }
    } catch (error) {
      console.error("Failed to load offer:", error);
      toast.error("Fehler beim Laden des Angebots");
      navigate("/angebote");
    } finally {
      setLoading(false);
    }
  };
  
  // Send offer for remote digital signature
  const handleSendForSignature = async () => {
    if (!offer?.customer) {
      toast.error("Kundeninformationen fehlen");
      return;
    }
    
    if (!offer.customer.email) {
      toast.error("Kunde hat keine E-Mail-Adresse. Bitte zuerst Kundendaten aktualisieren.");
      return;
    }
    
    setSendingForSignature(true);
    try {
      // Create signing request
      const createResponse = await offersAPI.sendForSignature(id, {
        offer_id: id,
        recipient_name: `${offer.customer.first_name || ''} ${offer.customer.last_name || ''}`.trim() || offer.customer.company_name,
        recipient_email: offer.customer.email,
        expires_in_days: 14
      });
      
      const requestId = createResponse.data?.id;
      if (!requestId) throw new Error("Signing request nicht erstellt");
      
      // Send email
      await signingRequestsAPI.sendEmail(requestId);
      
      toast.success("Angebot wurde zur Unterschrift versendet");
      setSendForSignatureDialogOpen(false);
      
      // Reload to get updated signing status
      loadOffer();
    } catch (error) {
      console.error("Failed to send for signature:", error);
      
      // Handle structured error responses
      const errorDetail = error.response?.data?.detail;
      let errorMessage = "Fehler beim Versenden zur Unterschrift";
      
      if (typeof errorDetail === 'object') {
        // Structured error response
        const errorCode = errorDetail.error_code;
        
        switch (errorCode) {
          case 'SIGNING_REQUEST_EXISTS':
            errorMessage = "Für dieses Angebot existiert bereits eine aktive Signaturanfrage";
            toast.error(errorMessage, {
              action: {
                label: 'Zur Signatur-Verwaltung',
                onClick: () => navigate('/signaturen')
              }
            });
            setSendForSignatureDialogOpen(false);
            loadOffer(); // Reload to show existing request
            return;
          case 'EMAIL_SEND_FAILED':
            errorMessage = errorDetail.message || "E-Mail konnte nicht gesendet werden";
            break;
          case 'OFFER_NOT_FOUND':
            errorMessage = "Angebot nicht gefunden";
            break;
          default:
            errorMessage = errorDetail.message || errorMessage;
        }
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      }
      
      toast.error(errorMessage);
    } finally {
      setSendingForSignature(false);
    }
  };
  
  // Get signing status badge
  const getSigningStatusBadge = () => {
    if (!signingRequest) return null;
    
    const statusConfig = {
      sent: { label: "Zur Unterschrift gesendet", color: "bg-blue-100 text-blue-800", icon: Mail },
      otp_sent: { label: "OTP gesendet", color: "bg-blue-100 text-blue-800", icon: Mail },
      opened: { label: "Vom Kunden geöffnet", color: "bg-purple-100 text-purple-800", icon: Eye },
      otp_verified: { label: "OTP verifiziert", color: "bg-indigo-100 text-indigo-800", icon: Check },
      completed_digital: { label: "Digital unterschrieben", color: "bg-green-100 text-green-800", icon: CheckCircle },
      completed_manual: { label: "Manuell unterschrieben", color: "bg-green-100 text-green-800", icon: CheckCircle },
    };
    
    const config = statusConfig[signingRequest.status] || { label: signingRequest.status, color: "bg-gray-100 text-gray-800", icon: Clock };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  // PDF Generation
  const handleGeneratePdf = async () => {
    setGeneratingPdf(true);
    setPdfLoading(true);
    setPdfLoadError(false);  // Reset error state
    try {
      // Fetch PDF as Blob and create URL for react-pdf
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/offers/${id}/pdf?token=${token}`);
      
      if (!response.ok) {
        throw new Error('PDF konnte nicht geladen werden');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfData(blobUrl);
      setCurrentPage(1);
      setPdfDialogOpen(true);
      // Reload offer to get updated pdf_generated status
      await loadOffer();
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      toast.error(getErrorMessage(error, "Fehler beim Erstellen des PDFs"));
    } finally {
      setGeneratingPdf(false);
      setPdfLoading(false);
    }
  };

  const [refreshingPdf, setRefreshingPdf] = useState(false);
  const handleRefreshPdf = async () => {
    setRefreshingPdf(true);
    try {
      await offersAPI.refreshPdf(id);
      toast.success("Wirtschaftlichkeitsberechnung aktualisiert. PDF wird neu erstellt...");
      await loadOffer();
      // Direkt neues PDF generieren
      await handleGeneratePdf();
    } catch (error) {
      console.error("Failed to refresh PDF:", error);
      toast.error(getErrorMessage(error, "Fehler beim Aktualisieren"));
    } finally {
      setRefreshingPdf(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  const handleDownloadPdf = async () => {
    try {
      const blobUrl = await offersAPI.downloadPdf(id);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `Angebot_${offer.offer_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      toast.success("PDF wird heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Herunterladen des PDFs");
    }
  };

  const handleOpenPdfInNewTab = async () => {
    try {
      toast.info("PDF wird geladen...");
      const blobUrl = await offersAPI.downloadPdf(id);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error("Failed to open PDF:", error);
      toast.error("Fehler beim Öffnen des PDFs");
    }
  };

  const handleArchive = async () => {
    try {
      await offersAPI.archive(id);
      toast.success("Angebot archiviert");
      navigate("/angebote");
    } catch (error) {
      toast.error("Fehler beim Archivieren");
    }
  };

  const handleUnarchive = async () => {
    try {
      await offersAPI.unarchive(id);
      toast.success("Angebot wiederhergestellt");
      loadOffer();
    } catch (error) {
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  // Send offer to customer via email
  const handleSendOfferEmail = async () => {
    if (!offer?.customer_email) {
      toast.error("Keine E-Mail-Adresse für den Kunden hinterlegt");
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await offersAPI.sendEmail(id);
      const msg = response?.data?.message || `Angebot wird an ${offer.customer_email} gesendet...`;
      toast.success(msg);
    } catch (error) {
      console.error("Failed to send offer email:", error);
      toast.error(getErrorMessage(error, "Fehler beim E-Mail-Versand"));
    } finally {
      setSendingEmail(false);
    }
  };

  // Opens the seller signature dialog instead of directly generating
  const handleGenerateContract = () => {
    setSellerSignatureDialogOpen(true);
    setHasSellerSignature(false);
  };

  // Actually submits the contract with seller signature
  const handleSubmitSellerSignature = async () => {
    if (!hasSellerSignature) {
      toast.error("Bitte unterschreiben Sie den Kaufvertrag");
      return;
    }
    
    setSendingContract(true);
    try {
      const canvas = sellerCanvasRef.current;
      const signatureData = canvas.toDataURL("image/png");
      
      const response = await offersAPI.generateContract(id, signatureData);
      toast.success(response.data.message);
      setSellerSignatureDialogOpen(false);
      loadOffer(); // Reload to show updated contract_generated_at
    } catch (error) {
      console.error("Failed to generate contract:", error);
      toast.error(getErrorMessage(error, "Fehler beim Erzeugen des Kaufvertrags"));
    } finally {
      setSendingContract(false);
    }
  };

  // Seller Signature Canvas Functions
  const getSellerCanvasCoordinates = (e) => {
    const canvas = sellerCanvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startSellerDrawing = (e) => {
    e.preventDefault();
    const canvas = sellerCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getSellerCanvasCoordinates(e);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsSellerDrawing(true);
  };

  const drawSeller = (e) => {
    if (!isSellerDrawing) return;
    e.preventDefault();
    
    const canvas = sellerCanvasRef.current;
    const ctx = canvas.getContext("2d");
    const coords = getSellerCanvasCoordinates(e);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSellerSignature(true);
  };

  const stopSellerDrawing = () => {
    setIsSellerDrawing(false);
  };

  const clearSellerSignature = () => {
    const canvas = sellerCanvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSellerSignature(false);
  };

  // Signature Canvas Functions (for customer)
  const getCanvasCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCanvasCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCanvasCoordinates(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleSign = async () => {
    if (!hasSignature) {
      toast.error("Bitte unterschreiben Sie im Feld");
      return;
    }

    setSigning(true);
    
    try {
      const canvas = canvasRef.current;
      const signatureData = canvas.toDataURL("image/png");
      
      await offersAPI.sign(id, signatureData);
      toast.success("Angebot erfolgreich unterschrieben");
      setSignatureDialogOpen(false);
      loadOffer();
    } catch (error) {
      console.error("Failed to sign offer:", error);
      toast.error(getErrorMessage(error, "Fehler beim Unterschreiben"));
    } finally {
      setSigning(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await offersAPI.updateStatus(id, newStatus);
      toast.success("Status aktualisiert");
      loadOffer();
    } catch (error) {
      console.error("Failed to update status:", error);
      toast.error("Fehler beim Aktualisieren des Status");
    }
  };

  const canSign = offer?.pdf_generated || offer?.status === "angenommen";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!offer) return null;

  const statusLabel = offer.status === "angenommen" ? "Angenommen" : 
                      offer.status === "abgelehnt" ? "Abgelehnt" : 
                      offer.offer_sent_at ? "Gesendet" : "Offen";
  
  const statusClass = offer.status === "angenommen" ? "angebot_angenommen" : 
                      offer.status === "abgelehnt" ? "angebot_abgelehnt" : 
                      offer.offer_sent_at ? "angebot_gesendet" : "angebot_erstellt";

  return (
    <div className="space-y-6 animate-slide-in" data-testid="offer-detail">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/angebote")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold font-['Manrope']">{offer.offer_number}</h1>
              <Badge className={`status-${statusClass}`}>
                {statusLabel}
              </Badge>
              <IncognitoBadge active={offer.is_incognito} size="md" />
              {offer.offer_sent_at && (
                <Badge variant="outline" className="text-blue-600 border-blue-600">
                  <Mail className="h-3 w-3 mr-1" />
                  {new Date(offer.offer_sent_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </Badge>
              )}
              {offer.pdf_generated && (
                <Badge variant="outline" className="text-green-600 border-green-600">
                  <FileCheck className="h-3 w-3 mr-1" />
                  PDF erstellt
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">
              Projekt {offer.project_number} | {offer.customer_name}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <IncognitoToggleButton user={user} docType="offer" docId={offer.id} active={offer.is_incognito} onToggled={loadOffer} />
          {/* Edit Button - only visible when PDF not generated and not signed */}
          {offer.status === "erstellt" && !offer.pdf_generated && !offer.signature && (
            <Button
              variant="outline"
              onClick={() => navigate(`/angebote/${id}/bearbeiten`)}
              data-testid="edit-offer-btn"
            >
              <Edit className="h-4 w-4 mr-2" />
              Bearbeiten
            </Button>
          )}
          
          {/* PDF Button - always visible for unsigned offers */}
          {offer.status === "erstellt" && (
            <>
              <Button
                variant="outline"
                onClick={handleGeneratePdf}
                disabled={generatingPdf}
                data-testid="generate-pdf-btn"
              >
                {generatingPdf ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                {offer.pdf_generated ? "PDF anzeigen" : "PDF erstellen"}
              </Button>
              
              {/* PDF aktualisieren - nur wenn bereits generiert und nicht unterschrieben */}
              {offer.pdf_generated && !offer.signature && (
                <Button
                  variant="outline"
                  onClick={handleRefreshPdf}
                  disabled={refreshingPdf || generatingPdf}
                  data-testid="refresh-pdf-btn"
                  title="Berechnung aktualisieren und PDF neu erstellen"
                >
                  {refreshingPdf ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  PDF aktualisieren
                </Button>
              )}
            </>
          )}
          
          {offer.status === "erstellt" && (
            <>
              <Button
                variant="outline"
                onClick={() => handleStatusChange("abgelehnt")}
                className="text-destructive"
                data-testid="reject-offer-btn"
              >
                <X className="h-4 w-4 mr-2" />
                Ablehnen
              </Button>
              
              {/* Remote Digital Signing - Primary Action */}
              {!signingRequest && (
                <Button
                  onClick={() => {
                    if (!offer.pdf_generated) {
                      toast.error("Bitte erstellen Sie zuerst das PDF");
                      return;
                    }
                    setSendForSignatureDialogOpen(true);
                  }}
                  className={`${offer.pdf_generated ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"}`}
                  data-testid="send-for-signature-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Zur Unterschrift senden
                </Button>
              )}
              
              {/* Show signing status if request exists */}
              {signingRequest && (
                <div className="flex items-center gap-2">
                  {getSigningStatusBadge()}
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                  >
                    <Link to="/signaturen">
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Details
                    </Link>
                  </Button>
                </div>
              )}
              
              {/* Local Signature - Secondary Option (Vor-Ort) */}
              <Button
                variant="outline"
                onClick={() => {
                  if (!canSign) {
                    toast.error("Bitte erstellen Sie zuerst das PDF, um es dem Kunden zu zeigen");
                    return;
                  }
                  setSignatureDialogOpen(true);
                }}
                disabled={!canSign}
                data-testid="sign-offer-btn"
                title="Vor-Ort Unterschrift"
              >
                <Pen className="h-4 w-4 mr-2" />
                Vor-Ort unterschreiben
              </Button>
            </>
          )}
          
          {/* Download PDF for signed offers */}
          {offer.signature && (
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const blobUrl = await offersAPI.downloadPdf(id);
                  // Create download link
                  const link = document.createElement('a');
                  link.href = blobUrl;
                  link.download = `Angebot_${offer.offer_number}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                } catch (error) {
                  toast.error("Fehler beim Herunterladen des PDFs");
                }
              }}
              data-testid="download-pdf-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              PDF herunterladen
            </Button>
          )}
          
          {/* Generate Contract Button - For signed offers without contract */}
          {/* Admins/Superadmins can generate for any offer, Vertriebler only for their own */}
          {offer.signature && !offer.contract_generated_at && (
            user?.role === "admin" || 
            user?.role === "superadmin" || 
            (user?.role === "vertriebler" && offer.created_by === user?.id)
          ) && (
            <Button
              onClick={handleGenerateContract}
              disabled={sendingContract}
              className="bg-green-600 hover:bg-green-700"
              data-testid="generate-contract-btn"
            >
              {sendingContract ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileCheck className="h-4 w-4 mr-2" />
              )}
              Kaufvertrag erzeugen
            </Button>
          )}
          
          {/* Contract Generated Badge */}
          {offer.contract_generated_at && (
            <Badge variant="outline" className="text-green-600 border-green-600">
              <FileCheck className="h-3 w-3 mr-1" />
              Kaufvertrag erzeugt
            </Badge>
          )}
          
          {/* Archive button for rejected offers */}
          {offer.status === "abgelehnt" && !offer.archived && (
            <Button
              variant="outline"
              onClick={handleArchive}
              className="text-muted-foreground"
              data-testid="archive-offer-btn"
            >
              <Archive className="h-4 w-4 mr-2" />
              Archivieren
            </Button>
          )}
          
          {/* Unarchive button for archived offers */}
          {offer.archived && (
            <Button
              variant="outline"
              onClick={handleUnarchive}
              className="text-muted-foreground"
              data-testid="unarchive-offer-btn"
            >
              <ArchiveRestore className="h-4 w-4 mr-2" />
              Wiederherstellen
            </Button>
          )}
        </div>
      </div>

      {/* Archived Banner */}
      {offer.archived && (
        <Card className="border-gray-300 bg-gray-50 dark:bg-gray-900/30 dark:border-gray-700">
          <CardContent className="flex items-center gap-3 py-4">
            <Archive className="h-5 w-5 text-gray-500" />
            <div>
              <p className="font-medium text-gray-700 dark:text-gray-300">Angebot archiviert</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Dieses Angebot wurde archiviert und erscheint nicht mehr in der Standardansicht.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Banner for unsigned offers without PDF */}
      {offer.status === "erstellt" && !offer.pdf_generated && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
          <CardContent className="flex items-center gap-3 py-4">
            <FileText className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-200">PDF noch nicht erstellt</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Erstellen Sie zuerst das PDF, um es dem Kunden zu zeigen, bevor dieser unterschreiben kann.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contract Generated Banner */}
      {offer.contract_generated_at && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/30 dark:border-green-800">
          <CardContent className="flex items-center gap-3 py-4">
            <FileCheck className="h-5 w-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-200">Kaufvertrag erzeugt</p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Der Kaufvertrag wurde am {new Date(offer.contract_generated_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} erzeugt.
                {offer.contract_sent_at && (
                  <> Versendet am {new Date(offer.contract_sent_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.</>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Kundendaten</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <p className="font-medium">{offer.salutation} {offer.customer_name}</p>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {offer.customer_address}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Intro Text */}
          {offer.intro_text && (
            <Card>
              <CardContent className="pt-6">
                <p className="whitespace-pre-wrap text-sm">{offer.intro_text}</p>
              </CardContent>
            </Card>
          )}

          {/* System Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Anlagenkonfiguration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Sun className="h-5 w-5 text-accent" />
                    <span className="font-medium">Module</span>
                  </div>
                  <p className="text-2xl font-bold font-['Manrope']">{offer.module_count}x</p>
                  <p className="text-sm text-muted-foreground">{offer.module_name}</p>
                  <p className="text-sm font-medium text-accent">{typeof offer.kwp === 'number' ? offer.kwp.toFixed(2) : offer.kwp} kWp</p>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Battery className="h-5 w-5 text-accent" />
                    <span className="font-medium">Speicher</span>
                  </div>
                  <p className="text-2xl font-bold font-['Manrope']">{offer.storage_size} kWh</p>
                  <p className="text-sm text-muted-foreground">{offer.storage_name}</p>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className="h-5 w-5 text-accent" />
                    <span className="font-medium">Wechselrichter</span>
                  </div>
                  <p className="text-lg font-bold font-['Manrope']">{offer.inverter_name}</p>
                </div>
              </div>

              {/* Additional Options */}
              <div className="pt-4 border-t space-y-2">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">
                  Zusatzoptionen
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  <div className={`p-3 rounded-lg ${offer.backup ? "bg-blue-100 dark:bg-blue-900/30" : "bg-muted"}`}>
                    <p className="text-sm font-medium">Backup</p>
                    <p className="text-xs text-muted-foreground">
                      {offer.backup ? "Ja (+400€)" : "Nein"}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${offer.meter_cabinet ? "bg-green-100 dark:bg-green-900/30" : "bg-muted"}`}>
                    <p className="text-sm font-medium">Zählerschrank</p>
                    <p className="text-xs text-muted-foreground">
                      {offer.meter_cabinet ? "Ja" : "Nein"}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${offer.grounding ? "bg-green-100 dark:bg-green-900/30" : "bg-muted"}`}>
                    <p className="text-sm font-medium">Erdung</p>
                    <p className="text-xs text-muted-foreground">
                      {offer.grounding ? "Ja" : "Nein"}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${offer.scaffolding !== "nein" ? "bg-green-100 dark:bg-green-900/30" : "bg-muted"}`}>
                    <p className="text-sm font-medium">Gerüst</p>
                    <p className="text-xs text-muted-foreground">
                      {offer.scaffolding === "nein" ? "Nein" : offer.scaffolding === "1_traufe" ? "1 Traufe" : "2 Traufe"}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${offer.wallbox !== "nein" ? "bg-green-100 dark:bg-green-900/30" : "bg-muted"}`}>
                    <p className="text-sm font-medium">Wallbox</p>
                    <p className="text-xs text-muted-foreground">
                      {offer.wallbox === "nein" ? "Nein" : offer.wallbox === "11kw" ? "11 kW" : "11 kW MID"}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Signature */}
          {offer.signature && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Check className="h-5 w-5 text-green-600" />
                  Unterschrieben
                </CardTitle>
                <CardDescription>
                  Zeitstempel: {new Date(offer.signature_timestamp).toLocaleString("de-DE")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-white rounded-lg p-4 border">
                  <img
                    src={offer.signature}
                    alt="Unterschrift"
                    className="max-h-32 mx-auto"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-2 font-mono">
                  Hash: {offer.signature_hash}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Price Summary */}
        <div>
          <Card className="sticky top-4">
            <CardHeader>
              <CardTitle className="font-['Manrope']">Preisübersicht</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Anlagenpreis</span>
                  <span>{formatCurrency(offer.base_price)}</span>
                </div>
                {offer.backup_price > 0 && (
                  <div className="flex justify-between text-blue-600">
                    <span>+ Backup / Notstrom</span>
                    <span>{formatCurrency(offer.backup_price)}</span>
                  </div>
                )}
                {offer.meter_cabinet_price > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>+ Zählerschrank</span>
                    <span>{formatCurrency(offer.meter_cabinet_price)}</span>
                  </div>
                )}
                {offer.grounding_price > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>+ Erdung</span>
                    <span>{formatCurrency(offer.grounding_price)}</span>
                  </div>
                )}
                {offer.scaffolding_price > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>+ Gerüst</span>
                    <span>{formatCurrency(offer.scaffolding_price)}</span>
                  </div>
                )}
                {offer.wallbox_price > 0 && (
                  <div className="flex justify-between text-muted-foreground">
                    <span>+ Wallbox</span>
                    <span>{formatCurrency(offer.wallbox_price)}</span>
                  </div>
                )}
                
                {offer.discount_amount > 0 && (
                  <>
                    <Separator />
                    <div className="flex justify-between text-green-600">
                      <span>- Rabatt ({offer.discount_percent}%)</span>
                      <span>-{formatCurrency(offer.discount_amount)}</span>
                    </div>
                  </>
                )}
                
                {/* Saisonaler Rabatt */}
                {offer.seasonal_discount_amount > 0 && (
                  <>
                    <Separator />
                    <div className="flex flex-col">
                      <div className="flex justify-between text-amber-600">
                        <span>- {offer.seasonal_discount_name || 'Saisonaler Rabatt'} ({offer.seasonal_discount_percent}%)</span>
                        <span>-{formatCurrency(offer.seasonal_discount_amount)}</span>
                      </div>
                      {offer.seasonal_discount_period && (
                        <span className="text-xs text-muted-foreground">
                          Gültig: {offer.seasonal_discount_period.replace(/(\d{4})-(\d{2})-(\d{2})/g, (_, y, m, d) => `${d}.${m}.${y}`).replace(' bis ', ' - ')}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              <Separator />

              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="font-semibold">Gesamtpreis</span>
                  <span className={`text-2xl font-bold font-['Manrope'] ${offer.payment_terms === "prepayment" ? "text-muted-foreground line-through" : "text-accent"}`}>
                    {formatCurrency(offer.display_price)}
                  </span>
                </div>
                
                {/* Skonto for prepayment */}
                {offer.payment_terms === "prepayment" && offer.skonto_amount > 0 && (
                  <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg space-y-2">
                    <div className="flex justify-between text-green-600 text-sm">
                      <span>- 5% Skonto (Vorkasse)</span>
                      <span>-{formatCurrency(offer.skonto_amount)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-sm">Endpreis</span>
                      <span className="text-xl font-bold font-['Manrope'] text-green-600">
                        {formatCurrency(offer.final_price)}
                      </span>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">inkl. MwSt.</p>
                
                {/* Payment Terms Info */}
                <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs font-medium mb-1">Zahlungsbedingungen:</p>
                  {offer.payment_terms === "prepayment" ? (
                    <p className="text-xs text-muted-foreground">100% Vorkasse nach Auftragsbestätigung</p>
                  ) : (
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      <p>60% nach Auftragsbestätigung</p>
                      <p>30% nach Fertigstellung Dacharbeiten</p>
                      <p>10% nach Inbetriebnahme & Abnahme</p>
                    </div>
                  )}
                </div>
                
                {/* Admin-Modus Switch - Only visible for admins, never for customers */}
                {isAdmin && marginData && (
                  <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Eye className="h-4 w-4 text-amber-600" />
                        <Label className="text-amber-800 dark:text-amber-200 font-medium text-sm">Admin-Modus</Label>
                      </div>
                      <Switch
                        checked={showMarginAdmin}
                        onCheckedChange={setShowMarginAdmin}
                        data-testid="admin-mode-switch"
                      />
                    </div>
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Zeigt die Margenberechnung an (nur für Admins)
                    </p>
                  </div>
                )}
                
                {/* Margin Info - Admin Only (only visible when Admin-Modus is ON) */}
                {isAdmin && marginData && showMarginAdmin && (
                  <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700" data-testid="margin-info">
                    <div className="flex items-center gap-2 mb-2">
                      {marginData.margin_amount >= 0 ? (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      ) : (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      )}
                      <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">Margenberechnung (Admin)</p>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Verkaufspreis</span>
                        <span>{formatCurrency(marginData.sale_price)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Einkaufskosten</span>
                        <span>-{formatCurrency(marginData.total_purchase_cost)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Installationskosten</span>
                        <span>-{formatCurrency(marginData.total_installation_cost)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Provision</span>
                        <span>-{formatCurrency(marginData.commission || 0)}</span>
                      </div>
                      <Separator className="my-1" />
                      <div className="flex justify-between text-muted-foreground">
                        <span>Gesamtkosten</span>
                        <span>{formatCurrency(marginData.total_cost)}</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>Marge</span>
                        <span className={marginData.margin_amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(marginData.margin_amount)} ({marginData.margin_percent?.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* PDF Preview Dialog with embedded viewer */}
      <Dialog open={pdfDialogOpen} onOpenChange={setPdfDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Angebot {offer.offer_number} - PDF Vorschau
            </DialogTitle>
            <DialogDescription>
              Zeigen Sie dem Kunden das Angebot und lassen Sie ihn anschließend unterschreiben.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-[500px] bg-gray-100 dark:bg-gray-800 rounded-lg overflow-auto flex flex-col items-center py-4">
            {pdfLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2">PDF wird geladen...</span>
              </div>
            ) : pdfData ? (
              <>
                {pdfLoadError ? (
                  // Fallback to iframe when react-pdf fails
                  <iframe
                    src={pdfData}
                    className="w-full h-[70vh] rounded-lg shadow-lg"
                    title="PDF Vorschau"
                  />
                ) : (
                  <>
                    <Document
                      file={pdfData}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(error) => {
                        console.error("PDF load error:", error);
                        setPdfLoadError(true);  // Switch to iframe fallback
                      }}
                      loading={
                        <div className="flex items-center justify-center h-64">
                          <Loader2 className="h-8 w-8 animate-spin text-primary" />
                        </div>
                      }
                    >
                      <Page 
                        pageNumber={currentPage} 
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        width={Math.min(700, window.innerWidth - 100)}
                        className="shadow-lg"
                      />
                    </Document>
                    {numPages > 1 && (
                  <div className="flex items-center gap-4 mt-4 bg-white dark:bg-gray-900 px-4 py-2 rounded-lg shadow">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">
                      Seite {currentPage} von {numPages}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                      disabled={currentPage >= numPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                  </>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full gap-4">
                <FileCheck className="h-16 w-16 text-green-500" />
                <p className="text-muted-foreground">PDF wird vorbereitet...</p>
              </div>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
            <div className="flex gap-2 flex-1">
              <Button variant="outline" size="sm" onClick={handleOpenPdfInNewTab}>
                <Eye className="h-4 w-4 mr-2" />
                In neuem Tab
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownloadPdf}>
                <Download className="h-4 w-4 mr-2" />
                Herunterladen
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSendOfferEmail}
                disabled={sendingEmail || !offer?.customer_email}
                title={!offer?.customer_email ? "Keine E-Mail-Adresse hinterlegt" : "Angebot per E-Mail senden"}
              >
                {sendingEmail ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Per E-Mail senden
              </Button>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPdfDialogOpen(false)}>
                Schließen
              </Button>
              {offer.status === "erstellt" && (
                <Button
                  onClick={() => {
                    setPdfDialogOpen(false);
                    setSignatureDialogOpen(true);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Pen className="h-4 w-4 mr-2" />
                  Jetzt unterschreiben
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Angebot unterschreiben</DialogTitle>
            <DialogDescription>
              Bitte unterschreiben Sie im Feld unten, um das Angebot anzunehmen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border rounded-lg overflow-hidden bg-white">
              <canvas
                ref={canvasRef}
                width={460}
                height={200}
                className="signature-canvas w-full cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={clearSignature}>
                <Eraser className="h-4 w-4 mr-2" />
                Löschen
              </Button>
              <p className="text-sm text-muted-foreground">
                {hasSignature ? "Unterschrift vorhanden" : "Bitte unterschreiben"}
              </p>
            </div>

            <div className="bg-muted p-3 rounded-lg text-sm">
              <p className="font-medium">Rechtssichere Signatur</p>
              <p className="text-muted-foreground text-xs mt-1">
                Diese Unterschrift wird mit Zeitstempel und kryptographischem Hash gesichert.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSignatureDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSign}
              disabled={signing || !hasSignature}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-sign-btn"
            >
              {signing ? "Signiere..." : "Angebot annehmen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seller Signature Dialog for Contract Generation */}
      <Dialog open={sellerSignatureDialogOpen} onOpenChange={setSellerSignatureDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Kaufvertrag unterschreiben</DialogTitle>
            <DialogDescription>
              Bitte unterschreiben Sie als Verkäufer den Kaufvertrag. Ihre Unterschrift wird zusammen mit der Kundenunterschrift im Kaufvertrag gespeichert.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              <p><strong>Verkäufer:</strong> {user?.first_name} {user?.last_name}</p>
              <p><strong>Angebot:</strong> {offer?.offer_number}</p>
            </div>
            
            <div className="border rounded-lg p-2 bg-white">
              <canvas
                ref={sellerCanvasRef}
                width={500}
                height={200}
                className="w-full border rounded cursor-crosshair touch-none"
                onMouseDown={startSellerDrawing}
                onMouseMove={drawSeller}
                onMouseUp={stopSellerDrawing}
                onMouseLeave={stopSellerDrawing}
                onTouchStart={startSellerDrawing}
                onTouchMove={drawSeller}
                onTouchEnd={stopSellerDrawing}
                data-testid="seller-signature-canvas"
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={clearSellerSignature}
              className="w-full"
              data-testid="clear-seller-signature-btn"
            >
              <Eraser className="h-4 w-4 mr-2" />
              Unterschrift löschen
            </Button>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSellerSignatureDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmitSellerSignature}
              disabled={!hasSellerSignature || sendingContract}
              className="bg-green-600 hover:bg-green-700"
              data-testid="confirm-seller-sign-btn"
            >
              {sendingContract ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Erzeuge Kaufvertrag...
                </>
              ) : (
                <>
                  <FileCheck className="h-4 w-4 mr-2" />
                  Kaufvertrag erzeugen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send for Remote Digital Signature Dialog */}
      <Dialog open={sendForSignatureDialogOpen} onOpenChange={setSendForSignatureDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Angebot zur Unterschrift senden</DialogTitle>
            <DialogDescription>
              Der Kunde erhält eine E-Mail mit einem sicheren Link zur digitalen Unterschrift.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted p-4 rounded-lg space-y-2">
              <p><strong>Empfänger:</strong> {offer?.customer?.first_name} {offer?.customer?.last_name}</p>
              <p>
                <strong>E-Mail:</strong>{" "}
                {offer?.customer?.email ? (
                  offer.customer.email
                ) : (
                  <span className="text-red-500 font-medium">Keine E-Mail hinterlegt!</span>
                )}
              </p>
              <p><strong>Angebot:</strong> {offer?.offer_number}</p>
            </div>
            
            {/* Warning if no email */}
            {!offer?.customer?.email && (
              <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-lg">
                <p className="font-medium">E-Mail-Adresse fehlt!</p>
                <p className="text-sm mt-1">
                  Bitte hinterlegen Sie zuerst eine E-Mail-Adresse für diesen Kunden unter{" "}
                  <a 
                    href={`/kunden/${offer?.customer?.id}`} 
                    className="underline font-medium"
                    onClick={() => setSendForSignatureDialogOpen(false)}
                  >
                    Kundendaten bearbeiten
                  </a>.
                </p>
              </div>
            )}
            
            {offer?.customer?.email && (
              <div className="text-sm text-muted-foreground">
                <p>Der Kunde erhält:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>E-Mail mit Link zur Signing-Seite</li>
                  <li>OTP-Code zur Verifizierung</li>
                  <li>Angebot-PDF zum Prüfen</li>
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSendForSignatureDialogOpen(false)}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleSendForSignature}
              disabled={sendingForSignature || !offer?.customer?.email}
              className={offer?.customer?.email ? "bg-blue-600 hover:bg-blue-700" : "bg-gray-400 cursor-not-allowed"}
              data-testid="confirm-send-signature-btn"
            >
              {sendingForSignature ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Jetzt senden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
